import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  X, Upload, FileText, CheckCircle, AlertCircle,
  Brain, Award, Target,
  Edit3, Users, Loader2, Plus, ExternalLink, Copy, Search
} from 'lucide-react';
import { useWebSocket } from '../../hooks/useWebSocket';
import { needsApi } from '../../services/api';
import { TagInput, Alert } from '../shared';
import { useSofteamboardStore } from '../../store/softeamboardStore';
import api from '../../services/api';
import RecruitingCreateModalShell from './RecruitingCreateModalShell';
import FormSectionTabs from './FormSectionTabs';

const createInitialFormData = () => ({
  title: '',
  client: '',
  priority: 'P2',
  description: '',
  category: '',
  quantity: 1,
  consultants: [],
  tjm: '',
  opportunitySalesforceUrl: '',
  missionSalesforceUrl: '',
  orderClients: [],
  orderClientsEmail: [],
  jd_seniority: '',
  jd_team: '',
  jd_project_name: '',
  jd_company: '',
  jd_location: '',
  jd_responsibilities: [],
  jd_required_skills: [],
  jd_desired_skills: [],
  jd_experience_years: '',
  jd_education: '',
  jd_soft_skills: [],
});

const createInitialDuplicateFormData = () => ({
  title: '',
  client: '',
  priority: 'P2',
  description: '',
  category: '',
  quantity: 1,
  consultants: [],
  tjm: '',
  opportunitySalesforceUrl: '',
  orderClients: [],
  orderClientsEmail: [],
  jdAnalysisStatus: 'PENDING',
  jdAnalysis: {},
  jdS3Url: null,
  jdAnalysisS3Url: null,
  jdSeniority: '',
  jdTeam: '',
  jdProjectName: '',
  jdCompany: '',
  jdLocation: '',
  jdResponsibilities: [],
  jdRequiredSkills: [],
  jdDesiredSkills: [],
  jdExperienceYears: '',
  jdEducation: '',
  jdSoftSkills: [],
  opportunityCreatedAt: null,
  missionSalesforceUrl: '',
  missionCreatedAt: null,
  propositionSalesforceId: null,
  propositionSalesforceUrl: null,
  propositionCreatedAt: null,
});

const requiredFieldErrors = data => {
  const nextErrors = {};
  const title = (data.title || '').trim();
  const client = (data.client || '').trim();
  if (!title) nextErrors.title = 'Title is required';
  else if (title.length < 3) nextErrors.title = 'Title must be at least 3 characters';
  if (!client) nextErrors.client = 'Client is required';
  else if (client.length < 2) nextErrors.client = 'Client must be at least 2 characters';
  if (!data.tjm || Number.isNaN(Number(data.tjm)) || Number(data.tjm) <= 0) {
    nextErrors.tjm = 'TJM must be a positive number';
  }
  return nextErrors;
};

const NeedCreateModal = ({ open, onClose, onSuccess }) => {
  const { needs, loadNeeds, createNeed } = useSofteamboardStore();
  
  const [activeTab, setActiveTab] = useState('upload');
  const [manualSection, setManualSection] = useState('principal');
  const [formData, setFormData] = useState(createInitialFormData);
  
  // ✅ NEW: Duplicate need state - FIXED to include ALL fields
  const [duplicateFormData, setDuplicateFormData] = useState(createInitialDuplicateFormData);
  
  const [jdFile, setJdFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [uploadingJD, setUploadingJD] = useState(false);
  const [analyzingJD, setAnalyzingJD] = useState(false);

  const [promptText, setPromptText] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [analysisJobId, setAnalysisJobId] = useState(null);
  const analysisJobIdRef = useRef(null);
  const [shouldClose, setShouldClose] = useState(false);
  const [completedData, setCompletedData] = useState(null);

  // Input states for array fields
  const [responsibilityInput, setResponsibilityInput] = useState('');
  // Order clients input states
  const [orderClientInput, setOrderClientInput] = useState('');
  const [orderClientEmailInput, setOrderClientEmailInput] = useState('');

  // ✅ NEW: Duplicate need search state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNeed, setSelectedNeed] = useState(null);
  const [searchingNeeds, setSearchingNeeds] = useState(false);

  // ✅ NEW: Load available needs for duplication
  useEffect(() => {
    if (activeTab === 'duplicate' && open) {
      loadAvailableNeeds();
    }
  }, [activeTab, open]);

  const loadAvailableNeeds = async () => {
    try {
      setSearchingNeeds(true);
      // Use store to load all needs if not already loaded
      if ((needs || []).length === 0) {
        await loadNeeds();
      }
    } catch (error) {
      console.error('Failed to load needs for duplication:', error);
    } finally {
      setSearchingNeeds(false);
    }
  };

  // ✅ NEW: Filter needs based on search term using store data
  const filteredNeeds = (needs || []).filter(need => 
    need.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    need.client?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    need.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ✅ NEW: Handle need selection for duplication - ADD DEBUG LOGGING
  const handleNeedSelect = (need) => {
    console.log('🔍 DEBUG: handleNeedSelect called with need:', {
      id: need.id,
      title: need.title,
      client: need.client,
      priority: need.priority,
      tjm: need.tjm,
      // JD Analysis fields
      jdAnalysisStatus: need.jdAnalysisStatus,
      jdAnalysis: need.jdAnalysis,
      jdS3Url: need.jdS3Url,
      jdAnalysisS3Url: need.jdAnalysisS3Url,
      // Individual JD fields
      jdSeniority: need.jdSeniority,
      jdTeam: need.jdTeam,
      jdProjectName: need.jdProjectName,
      jdCompany: need.jdCompany,
      jdLocation: need.jdLocation,
      jdResponsibilities: need.jdResponsibilities,
      jdRequiredSkills: need.jdRequiredSkills,
      jdDesiredSkills: need.jdDesiredSkills,
      jdExperienceYears: need.jdExperienceYears,
      jdEducation: need.jdEducation,
      jdSoftSkills: need.jdSoftSkills,
      // Salesforce fields
      opportunitySalesforceUrl: need.opportunitySalesforceUrl,
      missionSalesforceUrl: need.missionSalesforceUrl,
      propositionSalesforceUrl: need.propositionSalesforceUrl,
      // Order clients
      orderClients: need.orderClients,
      orderClientsEmail: need.orderClientsEmail,
      // All keys in the need object
      allKeys: Object.keys(need)
    });

    setSelectedNeed(need);
    setDuplicateFormData({
      title: need.title || '',
      client: need.client || '',
      priority: need.priority || 'P2',
      description: need.description || '',
      category: need.category || '',
      quantity: need.quantity || 1,
      consultants: need.consultants || [],
      tjm: need.tjm || '',
      opportunitySalesforceUrl: need.opportunitySalesforceUrl || '',
      opportunityCreatedAt: need.opportunityCreatedAt || null,
      missionSalesforceUrl: need.missionSalesforceUrl || '',
      missionCreatedAt: need.missionCreatedAt || null,
      propositionSalesforceId: need.propositionSalesforceId || '',
      propositionSalesforceUrl: need.propositionSalesforceUrl || '',
      propositionCreatedAt: need.propositionCreatedAt || null,
      orderClients: need.orderClients || [],
      orderClientsEmail: need.orderClientsEmail || [],
      
      // ✅ FIX: Copy ALL JD Analysis fields from original need
      jdAnalysisStatus: need.jdAnalysisStatus || 'PENDING',
      jdAnalysis: need.jdAnalysis || {},
      jdS3Url: need.jdS3Url || null,
      jdAnalysisS3Url: need.jdAnalysisS3Url || null,
      
      // ✅ FIX: Copy ALL individual JD fields from original need
      jdSeniority: need.jdSeniority || '',
      jdTeam: need.jdTeam || '',
      jdProjectName: need.jdProjectName || '',
      jdCompany: need.jdCompany || '',
      jdLocation: need.jdLocation || '',
      jdResponsibilities: need.jdResponsibilities || [],
      jdRequiredSkills: need.jdRequiredSkills || [],
      jdDesiredSkills: need.jdDesiredSkills || [],
      jdExperienceYears: need.jdExperienceYears || '',
      jdEducation: need.jdEducation || '',
      jdSoftSkills: need.jdSoftSkills || [],
    });

    console.log('🔍 DEBUG: duplicateFormData set to:', {
      title: duplicateFormData.title,
      client: duplicateFormData.client,
      priority: duplicateFormData.priority,
      tjm: duplicateFormData.tjm,
      // JD Analysis fields
      jdAnalysisStatus: duplicateFormData.jdAnalysisStatus,
      jdAnalysis: duplicateFormData.jdAnalysis,
      jdS3Url: duplicateFormData.jdS3Url,
      jdAnalysisS3Url: duplicateFormData.jdAnalysisS3Url,
      // Individual JD fields
      jdSeniority: duplicateFormData.jdSeniority,
      jdTeam: duplicateFormData.jdTeam,
      jdCompany: duplicateFormData.jdCompany,
      jdResponsibilities: duplicateFormData.jdResponsibilities,
      jdRequiredSkills: duplicateFormData.jdRequiredSkills,
      jdDesiredSkills: duplicateFormData.jdDesiredSkills,
      jdSoftSkills: duplicateFormData.jdSoftSkills,
      // All keys in duplicateFormData
      allKeys: Object.keys(duplicateFormData)
    });
  };

  // ✅ NEW: Handle duplicate form submission - ADD DEBUG LOGGING
  const handleDuplicateSubmit = async (e) => {
    e.preventDefault();
    
    console.log('🚀 DEBUG: handleDuplicateSubmit called');
    console.log('🚀 DEBUG: selectedNeed:', selectedNeed);
    console.log('🚀 DEBUG: duplicateFormData before validation:', duplicateFormData);
    
    if (!selectedNeed) {
      console.log('❌ DEBUG: No selectedNeed, setting error');
      setErrors({ duplicate: 'Please select a need to duplicate' });
      return;
    }

    const nextErrors = requiredFieldErrors(duplicateFormData);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setLoading(true);

    try {
      const needData = {
        title: duplicateFormData.title.trim(),
        client: duplicateFormData.client.trim(),
        priority: duplicateFormData.priority,
        description: duplicateFormData.description.trim() || null,
        category: duplicateFormData.category.trim() || null,
        quantity: duplicateFormData.quantity || 1,
        consultants: duplicateFormData.consultants,
        candidates: [],
        orderClients: duplicateFormData.orderClients,
        orderClientsEmail: duplicateFormData.orderClientsEmail,
        column: 'NO_CANDIDATE',
        progressPct: 0,
        tjm: Number.parseFloat(duplicateFormData.tjm),
        
        // ✅ FIX: Send ALL JD Analysis fields
        jdAnalysisStatus: duplicateFormData.jdAnalysisStatus,
        jdAnalysis: duplicateFormData.jdAnalysis,
        jdS3Url: duplicateFormData.jdS3Url,
        jdAnalysisS3Url: duplicateFormData.jdAnalysisS3Url,
        
        // ✅ FIX: Send ALL individual JD fields
        jdSeniority: duplicateFormData.jdSeniority,
        jdTeam: duplicateFormData.jdTeam,
        jdProjectName: duplicateFormData.jdProjectName,
        jdCompany: duplicateFormData.jdCompany,
        jdLocation: duplicateFormData.jdLocation,
        jdResponsibilities: duplicateFormData.jdResponsibilities,
        jdRequiredSkills: duplicateFormData.jdRequiredSkills,
        jdDesiredSkills: duplicateFormData.jdDesiredSkills,
        jdExperienceYears: duplicateFormData.jdExperienceYears,
        jdEducation: duplicateFormData.jdEducation,
        jdSoftSkills: duplicateFormData.jdSoftSkills,
        
        // ✅ FIX: Send ALL Salesforce integration fields
        opportunitySalesforceUrl: duplicateFormData.opportunitySalesforceUrl,
        opportunityCreatedAt: duplicateFormData.opportunityCreatedAt,
        missionSalesforceUrl: duplicateFormData.missionSalesforceUrl,
        missionCreatedAt: duplicateFormData.missionCreatedAt,
        propositionSalesforceId: duplicateFormData.propositionSalesforceId,
        propositionSalesforceUrl: duplicateFormData.propositionSalesforceUrl,
        propositionCreatedAt: duplicateFormData.propositionCreatedAt
      };

      console.log('🚀 DEBUG: Final needData being sent to API:', {
        ...needData,
        orderClientsCount: needData.orderClients.length,
        orderClientsEmailsCount: needData.orderClientsEmail.length,
        jdAnalysisData: {
          status: needData.jdAnalysisStatus,
          analysis: needData.jdAnalysis,
          seniority: needData.jdSeniority,
          team: needData.jdTeam,
          company: needData.jdCompany,
          responsibilities: needData.jdResponsibilities,
          requiredSkills: needData.jdRequiredSkills,
          desiredSkills: needData.jdDesiredSkills,
          softSkills: needData.jdSoftSkills
        },
        salesforceFields: {
          opportunitySalesforceUrl: needData.opportunitySalesforceUrl,
          missionSalesforceUrl: needData.missionSalesforceUrl,
          propositionSalesforceUrl: needData.propositionSalesforceUrl
        }
      });

      console.log('🚀 DEBUG: Calling needsApi.createNeed with data');
      const createdNeed = await createNeed(needData);
      
      console.log('✅ DEBUG: Need created successfully:', {
        id: createdNeed.id,
        title: createdNeed.title,
        jdAnalysisStatus: createdNeed.jdAnalysisStatus,
        jdAnalysis: createdNeed.jdAnalysis,
        jdSeniority: createdNeed.jdSeniority,
        jdTeam: createdNeed.jdTeam,
        jdCompany: createdNeed.jdCompany,
        jdResponsibilities: createdNeed.jdResponsibilities,
        jdRequiredSkills: createdNeed.jdRequiredSkills,
        jdDesiredSkills: createdNeed.jdDesiredSkills,
        jdSoftSkills: createdNeed.jdSoftSkills
      });
      
      resetForm();
      onSuccess(createdNeed);
      onClose();
    } catch (err) {
      console.error('❌ DEBUG: Error duplicating need:', err);
      console.error('❌ DEBUG: Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  // WebSocket for real-time updates - FIXED: Filter out irrelevant messages
  const handleWebSocketMessage = useCallback((message) => {
    // Handle both analysis-specific and general need events
    const messageType = message.type || message.event_type;
    const entityId = message.entity_id;
    const messageData = message.data || message.payload;
    
    // ✅ FIX: Only process messages for the current analysis job or need_analysis_complete
    if (messageType === 'need_analysis_complete') {
      console.log('📨 Need analysis complete message received:', message);

      if (!entityId || entityId !== analysisJobIdRef.current) return;

      setAnalyzingJD(false);
      setUploadingJD(false);

      const needData = messageData?.data || messageData || {};
      setCompletedData({
        ...needData,
        id: needData.id || entityId,
      });
      setShouldClose(true);

    } else if (messageType === 'need_analysis_progress') {
      // Handle progress messages if needed
      console.log('📊 Need analysis progress:', messageData);
    } else if (messageType === 'need_analysis_error') {
      if (!entityId || entityId !== analysisJobIdRef.current) return;
      // Handle error messages
      console.log('❌ Need analysis failed:', messageData);
      setAnalyzingJD(false);
      setUploadingJD(false);
      setErrors({ jdFile: messageData?.error || 'Analysis failed' });
    }
  }, []);
  
  // ✅ FIX: Use specific jobId for WebSocket subscription
  useWebSocket(
    localStorage.getItem('access_token'),
    handleWebSocketMessage,
    analysisJobId // ✅ ADD: Subscribe to specific job
  );

  // Fallback polling when WebSocket doesn't deliver completion
  useEffect(() => {
    if (!analysisJobId || !analyzingJD) return;

    const POLL_INTERVAL = 5000;
    const TIMEOUT = 120000;
    const startedAt = Date.now();
    const cancelled = { current: false };
    const intervalRef = { current: null };

    const poll = async () => {
      if (cancelled.current) return;

      if (Date.now() - startedAt >= TIMEOUT) {
        console.warn('⏱ Analysis polling timed out after 2 minutes');
        setAnalyzingJD(false);
        setUploadingJD(false);
        setErrors({ jdFile: 'Analysis timed out - please try again' });
        return;
      }

      try {
        const need = await needsApi.getNeed(analysisJobId);
        if (cancelled.current) return;
        if (need && need.jdAnalysisStatus === 'READY') {
          console.log('✅ Analysis completed via polling fallback');
          setAnalyzingJD(false);
          setUploadingJD(false);
          setCompletedData({ ...need, id: need.id || analysisJobId });
          setShouldClose(true);
          return;
        } else if (need && need.jdAnalysisStatus === 'ERROR') {
          console.log('❌ Analysis failed via polling fallback');
          setAnalyzingJD(false);
          setUploadingJD(false);
          setErrors({ jdFile: 'Analysis failed - please try again' });
          return;
        }
      } catch (error) {
        if (cancelled.current) return;
        console.log('🔄 Polling... need not ready yet');
      }

      intervalRef.current = setTimeout(poll, POLL_INTERVAL);
    };

    intervalRef.current = setTimeout(poll, POLL_INTERVAL);

    return () => {
      cancelled.current = true;
      if (intervalRef.current) clearTimeout(intervalRef.current);
    };
  }, [analysisJobId, analyzingJD]);

  // ✅ NEW: Handle WebSocket connection status
  useEffect(() => {
    const checkWebSocketConnection = () => {
      // Check if WebSocket is connected
      const wsManager = require('../../services/websocketManager').default;
      const isConnected = wsManager.getConnectionStatus();
      
      if (!isConnected && analyzingJD) {
        console.log('⚠️ WebSocket disconnected during analysis, attempting reconnection...');
        // The useWebSocket hook should handle reconnection automatically
      }
    };
    
    const interval = setInterval(checkWebSocketConnection, 5000); // Check every 5 seconds
    
    return () => clearInterval(interval);
  }, [analyzingJD]);

  // ✅ FIXED: Enhanced reset logic to prevent modal state conflicts
  useEffect(() => {
    if (open) {
      // Reset immediately when modal opens with a small delay to ensure clean state
      const timer = setTimeout(() => {
        resetForm();
      }, 10);
      return () => clearTimeout(timer);
    } else {
      // Also reset when modal closes to ensure clean state for next open
      const timer = setTimeout(() => {
        resetForm();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // ✅ FIXED: More comprehensive resetForm function
  const resetForm = () => {
    setFormData(createInitialFormData());
    setDuplicateFormData(createInitialDuplicateFormData());
    
    // Reset file and UI states
    setJdFile(null);
    setErrors({});
    setActiveTab('upload');
    setManualSection('principal');
    
    // Reset input states
    setResponsibilityInput('');
    setOrderClientInput('');
    setOrderClientEmailInput('');
    
    setPromptText('');

    // Reset duplicate states
    setSearchTerm('');
    setSelectedNeed(null);
    
    // ✅ CRITICAL: Reset all analysis-related states
    setAnalysisJobId(null);
    analysisJobIdRef.current = null;
    setShouldClose(false);
    setCompletedData(null);
    setUploadingJD(false);
    setAnalyzingJD(false);
    setDragActive(false);
    setLoading(false);
    
    // ✅ NEW: Clear any file input elements
    const fileInputs = document.querySelectorAll('input[type="file"]');
    fileInputs.forEach(input => {
      if (input) input.value = '';
    });
    
    console.log('🔄 NeedCreateModal - Form reset completed');
  };

  // ✅ FIXED: Enhanced completion handling
  useEffect(() => {
    if (shouldClose && completedData) {
      // Use setTimeout to ensure this happens after current render cycle
      const timer = setTimeout(() => {
        console.log('🎯 NeedCreateModal - Processing completion:', completedData);
        // AI-created needs open directly in their process tracker.
        onSuccess(completedData, { openProcessTracker: true });
        // Then reset and close with a small delay
        setTimeout(() => {
          resetForm();
          onClose();
        }, 50);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [shouldClose, completedData, onSuccess, onClose]);

  const validateField = (field, value) => {
    const newErrors = { ...errors };
    
    switch (field) {
      case 'title':
        if (!value.trim()) {
          newErrors.title = 'Title is required';
        } else if (value.trim().length < 3) {
          newErrors.title = 'Title must be at least 3 characters';
        } else {
          delete newErrors.title;
        }
        break;
      
      case 'client':
        if (!value.trim()) {
          newErrors.client = 'Client is required';
        } else if (value.trim().length < 2) {
          newErrors.client = 'Client must be at least 2 characters';
        } else {
          delete newErrors.client;
        }
        break;
      case 'tjm':
        if (!value || Number.isNaN(Number(value)) || Number(value) <= 0) {
          newErrors.tjm = 'TJM must be a positive number';
        } else {
          delete newErrors.tjm;
        }
        break;
      default:
        break;
    }
    
    setErrors(newErrors);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    validateField(field, value);
  };

  // Array field handlers
  const addArrayItem = (field, input, setInput) => {
    const item = input.trim();
    if (item && !formData[field].includes(item)) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], item]
      }));
      setInput('');
    }
  };

  const removeArrayItem = (field, itemToRemove) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter(item => item !== itemToRemove)
    }));
  };

  const handleKeyPress = (e, field, input, setInput) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addArrayItem(field, input, setInput);
    }
  };

  // Handle file drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      validateJDFile(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      validateJDFile(file);
    }
  };

  const validateJDFile = file => {
    if (!file) return;
    const allowedTypes = ['.pdf', '.doc', '.docx'];
    const fileExtension = '.' + (file.name || '').split('.').pop().toLowerCase();
    if (!allowedTypes.includes(fileExtension)) {
      setJdFile(null);
      setErrors({ jdFile: 'Sélectionnez un fichier PDF, DOC ou DOCX.' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setJdFile(null);
      setErrors({ jdFile: 'Le fichier ne doit pas dépasser 10 Mo.' });
      return;
    }
    setErrors({});
    setJdFile(file);
  };

  // Token freshness is handled by the api.js interceptor automatically
  const ensureFreshToken = () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('No access token found');
    }
    return token;
  };

  // AI Analysis with Job Description Upload
  const handleJDFileUpload = async (file) => {
    if (!file) return;

    // Ensure token is fresh before AI operation
    try {
      await ensureFreshToken();
    } catch (error) {
      console.error('Token refresh failed before AI operation:', error);
      setErrors({ jdFile: `Authentication error: ${error.message}` });
      return;
    }

    // Validate file type
    const allowedTypes = ['.pdf', '.doc', '.docx'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(fileExtension)) {
      setErrors({ jdFile: 'Please upload a PDF, DOC, or DOCX file' });
      return;
    }

    try {
      setUploadingJD(true);
      setAnalyzingJD(true);
      setErrors({});
      setJdFile(file);

      // Use the analysisIA API for file analysis
      const formData = new FormData();
      formData.append('files', file);
      formData.append('file_types', 'jd');
      formData.append('api_config_json', JSON.stringify({
        api_key: 'default',
        email: 'user@example.com',
        username: 'user'
      }));

      try {
        const response = await api.post('/analyses-ia/file-analyses', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        const analysisResponse = response.data;
      
        if (analysisResponse.results && analysisResponse.results.length > 0) {
          const result = analysisResponse.results[0];
          analysisJobIdRef.current = result.entity_id;
          setAnalysisJobId(result.entity_id);
          
          console.log('🔄 Analysis started, waiting for WebSocket updates...');
        } else {
          throw new Error('No analysis results received');
        }
      } catch (error) {
        console.error('AI analysis failed:', error);
        setErrors({ jdFile: `AI analysis failed: ${error.message}` });
        setUploadingJD(false);
        setAnalyzingJD(false);
      }

    } catch (error) {
      console.error('AI analysis failed:', error);
      setErrors({ jdFile: `AI analysis failed: ${error.message}` });
      setUploadingJD(false);
      setAnalyzingJD(false);
    }
  };

  // AI Analysis with Prompt (Describe mode)
  const handlePromptAnalysis = async () => {
    const trimmed = promptText.trim();
    if (trimmed.length < 20) {
      setErrors({ jdFile: 'Please enter at least 20 characters describing the position' });
      return;
    }

    try {
      await ensureFreshToken();
    } catch (error) {
      setErrors({ jdFile: `Authentication error: ${error.message}` });
      return;
    }

    try {
      setAnalyzingJD(true);
      setErrors({});

      const response = await api.post('/analyses-ia/prompt-analyses', {
        prompt: trimmed
      });

      const { entity_id } = response.data;
      analysisJobIdRef.current = entity_id;
      setAnalysisJobId(entity_id);

      console.log('🔄 Prompt analysis started, waiting for WebSocket updates...');
    } catch (error) {
      console.error('Prompt analysis failed:', error);
      setErrors({ jdFile: `AI analysis failed: ${error.response?.data?.detail || error.message}` });
      setAnalyzingJD(false);
    }
  };

  // Manual form submission
  const handleManualSubmit = async (e) => {
    e.preventDefault();
    
    const nextErrors = requiredFieldErrors(formData);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setLoading(true);

    try {
      const needData = {
        title: formData.title.trim(),
        client: formData.client.trim(),
        priority: formData.priority,
        description: formData.description.trim() || null,
        category: formData.category.trim() || null,
        quantity: formData.quantity || 1,
        consultants: formData.consultants,
        candidates: [],
        orderClients: formData.orderClients,
        orderClientsEmail: formData.orderClientsEmail,
        column: 'NO_CANDIDATE',
        progressPct: 0,
        tjm: Number.parseFloat(formData.tjm),
        opportunitySalesforceUrl: formData.opportunitySalesforceUrl.trim() || null,
        missionSalesforceUrl: formData.missionSalesforceUrl.trim() || null,
        jdSeniority: formData.jd_seniority.trim() || null,
        jdTeam: formData.jd_team.trim() || null,
        jdProjectName: formData.jd_project_name.trim() || null,
        jdCompany: formData.jd_company.trim() || null,
        jdLocation: formData.jd_location.trim() || null,
        jdResponsibilities: formData.jd_responsibilities,
        jdRequiredSkills: formData.jd_required_skills,
        jdDesiredSkills: formData.jd_desired_skills,
        jdExperienceYears: formData.jd_experience_years.trim() || null,
        jdEducation: formData.jd_education.trim() || null,
        jdSoftSkills: formData.jd_soft_skills,
      };

      console.log('🚀 Creating need with data:', {
        ...needData,
        orderClientsCount: needData.orderClients.length,
        orderClientsEmailsCount: needData.orderClientsEmail.length,
        orderClientsData: needData.orderClients.map((client, index) => ({
          name: client,
          email: needData.orderClientsEmail[index] || ''
        })),
        // ✅ UPDATED: Only log Opportunity and Mission fields
        salesforceFields: {
          opportunitySalesforceUrl: needData.opportunitySalesforceUrl
        }
      });

      const createdNeed = await createNeed(needData);
      
      resetForm();
      onSuccess(createdNeed);
      onClose();
    } catch (err) {
      console.error('Error creating need:', err);
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleApiError = (error) => {
    console.error('API Error:', error);
    if (error.response?.data?.detail) {
      setErrors({ general: error.response.data.detail });
    } else {
      setErrors({ general: error.message || 'An unexpected error occurred' });
    }
  };

  // Enhanced array field handlers for order clients
  const addOrderClient = () => {
    const clientName = orderClientInput.trim();
    const clientEmail = orderClientEmailInput.trim();
    
    if (clientName && !formData.orderClients.includes(clientName)) {
      setFormData(prev => ({
        ...prev,
        orderClients: [...prev.orderClients, clientName],
        orderClientsEmail: [...prev.orderClientsEmail, clientEmail || '']
      }));
      setOrderClientInput('');
      setOrderClientEmailInput('');
    }
  };

  const removeOrderClient = (index) => {
    setFormData(prev => ({
      ...prev,
      orderClients: prev.orderClients.filter((_, i) => i !== index),
      orderClientsEmail: prev.orderClientsEmail.filter((_, i) => i !== index)
    }));
  };

  const handleOrderClientKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addOrderClient();
    }
  };

  // ✅ FIX: Add missing handleClose function
  const handleClose = () => {
    if (!loading && !uploadingJD && !analyzingJD) {
      resetForm();
      setShouldClose(false);
      setCompletedData(null);
      onClose();
    }
  };

  // ✅ FIX: Add missing isFormValid function
  const isFormValid = formData.title.trim() &&
                      formData.client.trim() &&
                      Number(formData.tjm) > 0 &&
                       Object.keys(errors).length === 0;

  const methods = [
    { id: 'upload', label: 'Importer une fiche de poste', description: 'Extraire les informations d’un PDF ou DOCX', icon: Upload },
    { id: 'describe', label: 'Décrire le poste', description: 'Partir d’un brief en langage naturel', icon: Edit3 },
    { id: 'manual', label: 'Saisir le besoin', description: 'Renseigner chaque information manuellement', icon: FileText },
    { id: 'duplicate', label: 'Copier un besoin', description: 'Réutiliser un besoin existant', icon: Copy },
  ];
  const busy = loading || uploadingJD || analyzingJD;
  const primaryButtonClass = 'min-h-[44px] rounded-lg bg-blue-600 px-5 py-2.5 font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';
  const secondaryButtonClass = 'min-h-[44px] rounded-lg border border-gray-300 bg-white px-5 py-2.5 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2';
  const footerActions = (
    <>
      <button type="button" onClick={handleClose} disabled={busy} className={secondaryButtonClass}>Annuler</button>
      {activeTab === 'upload' && <button type="button" onClick={() => handleJDFileUpload(jdFile)} disabled={!jdFile || busy} className={primaryButtonClass}>{busy ? 'Analyse en cours…' : 'Créer depuis le document'}</button>}
      {activeTab === 'describe' && <button type="button" onClick={handlePromptAnalysis} disabled={promptText.trim().length < 20 || busy} className={primaryButtonClass}>{busy ? 'Analyse en cours…' : 'Créer depuis la description'}</button>}
      {activeTab === 'manual' && <button type="submit" form="need-manual-form" disabled={!isFormValid || busy} className={primaryButtonClass}>{loading ? 'Création en cours…' : 'Créer le besoin'}</button>}
      {activeTab === 'duplicate' && <button type="submit" form="need-duplicate-form" disabled={!selectedNeed || loading} className={primaryButtonClass}>{loading ? 'Création en cours…' : 'Créer la copie'}</button>}
    </>
  );

  return (
    <RecruitingCreateModalShell
      open={open}
      onClose={handleClose}
      title="Créer un besoin"
      subtitle="Choisissez la méthode la plus rapide selon les informations dont vous disposez."
      icon={Plus}
      methods={methods}
      activeMethod={activeTab}
      onMethodChange={setActiveTab}
      busy={busy}
      footerNote={activeTab === 'upload' ? (jdFile ? '1 fichier prêt · analyse au clic uniquement' : 'PDF, DOC ou DOCX · 10 Mo maximum') : 'Aucune donnée n’est créée avant validation.'}
      footerActions={footerActions}
    >
            {/* General Error */}
            {errors.general && (
              <div className="mb-6">
                <Alert variant="error">{errors.general}</Alert>
              </div>
            )}

            {/* AI Analysis Tab */}
            {(activeTab === 'upload' || activeTab === 'describe') && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {activeTab === 'upload' ? 'Importer une fiche de poste' : 'Décrire le poste'}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    {activeTab === 'upload' ? 'Sélectionnez un document à analyser après confirmation.' : 'Décrivez le besoin en langage naturel pour générer une proposition structurée.'}
                  </p>
                </div>

                {/* Upload Mode */}
                {activeTab === 'upload' && (
                  <div
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    dragActive
                      ? 'border-blue-400 bg-blue-50'
                      : uploadingJD || analyzingJD
                      ? 'border-gray-300 bg-gray-50'
                      : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                  }`}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                    {uploadingJD || analyzingJD ? (
                      <div className="space-y-4">
                      <div className="flex items-center justify-center">
                          <Brain className="animate-pulse text-purple-600" size={48} />
                      </div>
                        <div>
                          <p className="text-lg font-medium text-gray-900">
                             {uploadingJD ? 'Envoi du fichier…' : 'Analyse en cours…'}
                          </p>
                          <p className="text-sm text-gray-600">
                             Cette opération peut prendre quelques minutes. La fenêtre se fermera automatiquement.
                          </p>
                    </div>
                    </div>
                  ) : jdFile ? (
                      <div className="space-y-4">
                      <div className="flex items-center justify-center">
                          <CheckCircle className="text-green-600" size={48} />
                        </div>
                        <div>
                          <p className="text-lg font-medium text-green-600">
                            Fichier prêt : {jdFile.name}
                          </p>
                          <p className="text-sm text-gray-600">
                            Aucune analyse ne démarre avant votre confirmation.
                          </p>
                        </div>
                    </div>
                  ) : (
                      <div className="space-y-4">
                      <div className="flex items-center justify-center">
                          <Upload className="text-gray-400" size={48} />
                      </div>
                      <div>
                          <p className="text-lg font-medium text-gray-900 mb-2">
                            Déposez la fiche de poste ici
                          </p>
                          <p className="text-sm text-gray-600 mb-4">
                            ou sélectionnez un fichier sur votre ordinateur
                          </p>
                          <label className="inline-flex min-h-[44px] cursor-pointer items-center rounded-lg bg-blue-600 px-6 py-3 text-white hover:bg-blue-700 focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2">
                          <Upload size={16} className="mr-2" />
                           Choisir une fiche de poste
                          <input
                            type="file"
                            onChange={handleFileSelect}
                            className="hidden"
                              accept=".pdf,.doc,.docx"
                            disabled={uploadingJD || analyzingJD}
                          />
                        </label>
                      </div>
                      <p className="text-xs text-gray-500">
                          Formats acceptés : PDF, DOC, DOCX (10 Mo maximum)
                      </p>
                    </div>
                  )}
                </div>
                )}

                {/* Describe Mode */}
                {activeTab === 'describe' && (
                  <div className="space-y-4">
                    {analyzingJD ? (
                      <div className="border-2 border-dashed border-gray-300 bg-gray-50 rounded-lg p-8 text-center">
                        <div className="space-y-4">
                          <div className="flex items-center justify-center">
                            <Brain className="animate-pulse text-purple-600" size={48} />
                          </div>
                          <div>
                            <p className="text-lg font-medium text-gray-900">
                               Analyse en cours…
                            </p>
                            <p className="text-sm text-gray-600">
                               La fiche structurée est en cours de génération. La fenêtre se fermera automatiquement.
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => { setAnalyzingJD(false); setAnalysisJobId(null); }}
                            className="mt-2 text-sm text-gray-500 hover:text-gray-700 underline"
                          >
                             Annuler l’analyse
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div>
                          <label htmlFor="prompt-text" className="block text-sm font-medium text-gray-700 mb-2">
                            Description du besoin
                          </label>
                          <textarea
                            id="prompt-text"
                            value={promptText}
                            onChange={(e) => setPromptText(e.target.value)}
                            rows={6}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-y"
                             placeholder="Ex. : Lead Developer Java, 8 ans d’expérience, Marseille, équipe de 5 personnes, Spring Boot et AWS…"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            {promptText.length < 20
                               ? `${20 - promptText.length} caractères supplémentaires requis (minimum 20)`
                               : `${promptText.length} caractères`}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                )}

              {errors.jdFile && (
                  <p className="text-sm text-red-600 text-center" role="alert">
                  {errors.jdFile}
                </p>
              )}
            </div>
            )}

            {/* Manual Entry Tab */}
            {activeTab === 'manual' && (
              <form id="need-manual-form" onSubmit={handleManualSubmit} className="flex flex-col gap-8">
                <FormSectionTabs
                  tabs={[
                    { id: 'principal', label: 'Principal' },
                    { id: 'complementaire', label: 'Complémentaire' },
                    { id: 'integrations', label: 'Intégrations' },
                  ]}
                  activeTab={manualSection}
                  onChange={setManualSection}
                  label="Rubriques du besoin"
                  idPrefix="need-sections"
                />
                {/* Basic Information */}
                <div id="need-sections-principal-panel" role="tabpanel" aria-labelledby="need-sections-principal-tab" className={manualSection === 'principal' ? 'space-y-4' : 'hidden'}>
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <FileText className="mr-2" size={20} />
                    Informations essentielles
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Title Field */}
                    <div>
                      <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                        Titre du besoin <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="title"
                        type="text"
                        value={formData.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.title ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                        placeholder="e.g., Senior React Developer"
                        disabled={loading}
                        required
                      />
                      {errors.title && (
                        <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                      )}
                    </div>

                    {/* Client Field */}
                    <div>
                      <label htmlFor="client" className="block text-sm font-medium text-gray-700 mb-2">
                        Client <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="client"
                        type="text"
                        value={formData.client}
                        onChange={(e) => handleInputChange('client', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.client ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                        placeholder="e.g., Acme Corporation"
                        disabled={loading}
                        required
                      />
                      {errors.client && (
                        <p className="mt-1 text-sm text-red-600">{errors.client}</p>
                      )}
                    </div>

                    {/* Priority Field */}
                    <div>
                      <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-2">
                        Priorité
                      </label>
                      <select
                        id="priority"
                        value={formData.priority}
                        onChange={(e) => handleInputChange('priority', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        disabled={loading}
                      >
                         <option value="P1">P1 - Priorité haute</option>
                         <option value="P2">P2 - Priorité moyenne</option>
                         <option value="P3">P3 - Priorité basse</option>
                      </select>
                    </div>

                    {/* TJM Field */}
                    <div>
                      <label htmlFor="tjm" className="block text-sm font-medium text-gray-700 mb-2">
                        TJM € <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="tjm"
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={formData.tjm}
                        onChange={(e) => handleInputChange('tjm', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.tjm ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                        placeholder="e.g., 650"
                        disabled={loading}
                        required
                      />
                      {errors.tjm && (
                        <p className="mt-1 text-sm text-red-600">{errors.tjm}</p>
                      )}
                    </div>
                  </div>

                  {/* Description Field */}
                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                      Description générale
                    </label>
                    <textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                         placeholder="Décrivez le contexte et les objectifs de la mission…"
                      disabled={loading}
                    />
                  </div>

                  <h3 className="pt-2 text-lg font-medium text-gray-900">Classification</h3>
                  {/* Category and Quantity Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                        Catégorie
                      </label>
                      <select
                        id="category"
                        value={formData.category || ''}
                        onChange={(e) => handleInputChange('category', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        disabled={loading}
                      >
                        <option value="">Sélectionner une catégorie…</option>
                        <option value="developer">Développement</option>
                        <option value="test">Test</option>
                        <option value="functionnal">Fonctionnel</option>
                        <option value="IT">IT</option>
                        <option value="devops">DevOps</option>
                        <option value="others">Autres</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-2">
                        Quantité
                      </label>
                      <input
                        type="number"
                        id="quantity"
                        min="1"
                        value={formData.quantity}
                        onChange={(e) => handleInputChange('quantity', Number.parseInt(e.target.value) || 1)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="1"
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>

                <div id="need-sections-integrations-panel" role="tabpanel" aria-labelledby="need-sections-integrations-tab" className={manualSection === 'integrations' ? 'space-y-4' : 'hidden'}>
                {/* ✅ UPDATED: Salesforce Integration Section - Remove proposition fields */}
                <details open className="rounded-xl border border-gray-200 bg-white p-4">
                  <summary className="min-h-[44px] cursor-pointer font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-600">
                    Intégrations Salesforce <span className="text-sm font-normal text-gray-500">(optionnel)</span>
                  </summary>
                  
                  <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl p-4 border border-orange-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="opportunitySalesforceUrl" className="block text-sm font-medium text-gray-700 mb-2">
                          URL de l’opportunité Salesforce
                        </label>
                        <input
                          type="url"
                          id="opportunitySalesforceUrl"
                          value={formData.opportunitySalesforceUrl}
                          onChange={(e) => handleInputChange('opportunitySalesforceUrl', e.target.value)}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          placeholder="https://softeam.lightning.force.com/lightning/r/Opportunity/..."
                        />
                      </div>

                      <div>
                        <label htmlFor="mission-salesforce-url" className="block text-sm font-medium text-gray-700 mb-2">
                          URL de mission Salesforce
                        </label>
                        <div className="flex">
                          <input
                            id="mission-salesforce-url"
                            type="url"
                            value={formData.missionSalesforceUrl}
                            onChange={(e) => handleInputChange('missionSalesforceUrl', e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            placeholder="https://domain.lightning.force.com/..."
                            disabled={loading}
                          />
                          {formData.missionSalesforceUrl && (
                            <button
                              type="button"
                              onClick={() => window.open(formData.missionSalesforceUrl, '_blank')}
                              className="px-3 py-2 bg-orange-600 text-white rounded-r-md hover:bg-orange-700 transition-colors"
                              title="Open Mission in Salesforce"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-orange-800">
                          <strong>Note :</strong> ces champs peuvent être renseignés manuellement ou automatiquement lors de la création des enregistrements Salesforce.
                        </div>
                      </div>
                    </div>
                  </div>
                </details>

                {/* Order Clients Section - NEW */}
                <details open className="rounded-xl border border-gray-200 bg-white p-4">
                  <summary className="min-h-[44px] cursor-pointer font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-600">
                    Contacts de commande <span className="text-sm font-normal text-gray-500">(optionnel)</span>
                  </summary>
                  
                  <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-4 border border-indigo-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label htmlFor="order-client-name" className="block text-sm font-medium text-gray-700 mb-2">
                          Nom du contact
                        </label>
                        <input
                          id="order-client-name"
                          type="text"
                          value={orderClientInput}
                          onChange={(e) => setOrderClientInput(e.target.value)}
                          onKeyPress={handleOrderClientKeyPress}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          placeholder="e.g., John Doe"
                          disabled={loading}
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="order-client-email" className="block text-sm font-medium text-gray-700 mb-2">
                          E-mail du contact <span className="text-gray-400">(optionnel)</span>
                        </label>
                        <input
                          id="order-client-email"
                          type="email"
                          value={orderClientEmailInput}
                          onChange={(e) => setOrderClientEmailInput(e.target.value)}
                          onKeyPress={handleOrderClientKeyPress}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          placeholder="e.g., john.doe@company.com"
                          disabled={loading}
                        />
                      </div>
                    </div>
                    
                    <button
                      type="button"
                      onClick={addOrderClient}
                      disabled={!orderClientInput.trim() || loading}
                      className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Plus size={16} className="inline mr-2" />
                      Ajouter le contact de commande
                    </button>
                    
                    {formData.orderClients.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <div className="text-sm font-medium text-gray-700 mb-2">
                          Contacts ajoutés ({formData.orderClients.length}) :
                        </div>
                        {formData.orderClients.map((client, index) => (
                          <div key={index} className="flex items-center justify-between bg-white p-3 rounded-lg border border-indigo-100">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                                {client.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">{client}</div>
                                {formData.orderClientsEmail[index] && (
                                  <div className="text-sm text-gray-600">{formData.orderClientsEmail[index]}</div>
                                )}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeOrderClient(index)}
                              className="text-red-600 hover:text-red-800 p-1"
                              disabled={loading}
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </details>
                </div>

                {/* Position Details */}
                <div id="need-sections-complementaire-panel" role="tabpanel" aria-labelledby="need-sections-complementaire-tab" className={manualSection === 'complementaire' ? 'space-y-4' : 'hidden'}>
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <Target className="mr-2" size={20} />
                    Poste et mission
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="jd_experience_years" className="block text-sm font-medium text-gray-700 mb-2">Expérience requise (années)</label>
                      <input id="jd_experience_years" type="number" value={formData.jd_experience_years} onChange={(e) => handleInputChange('jd_experience_years', e.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500" disabled={loading} min="0" max="50" />
                    </div>
                    <div>
                      <label htmlFor="jd_seniority" className="block text-sm font-medium text-gray-700 mb-2">
                        Niveau de séniorité
                      </label>
                      <input
                        id="jd_seniority"
                        type="text"
                        value={formData.jd_seniority}
                        onChange={(e) => handleInputChange('jd_seniority', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., Senior, Mid-level, Junior"
                        disabled={loading}
                      />
                    </div>

                    <div>
                      <label htmlFor="jd_team" className="block text-sm font-medium text-gray-700 mb-2">
                        Équipe
                      </label>
                      <input
                        id="jd_team"
                        type="text"
                        value={formData.jd_team}
                        onChange={(e) => handleInputChange('jd_team', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., Engineering, Product"
                        disabled={loading}
                      />
                    </div>

                    <div>
                      <label htmlFor="jd_project_name" className="block text-sm font-medium text-gray-700 mb-2">
                        Nom du projet
                      </label>
                      <input
                        id="jd_project_name"
                        type="text"
                        value={formData.jd_project_name}
                        onChange={(e) => handleInputChange('jd_project_name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., Mobile App Redesign"
                        disabled={loading}
                      />
                    </div>

                    <div>
                      <label htmlFor="jd_location" className="block text-sm font-medium text-gray-700 mb-2">
                        Localisation
                      </label>
                      <input
                        id="jd_location"
                        type="text"
                        value={formData.jd_location}
                        onChange={(e) => handleInputChange('jd_location', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., Paris, Remote, Hybrid"
                        disabled={loading}
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label htmlFor="jd_education" className="block text-sm font-medium text-gray-700 mb-2">
                        Formation requise
                      </label>
                      <input
                        id="jd_education"
                        type="text"
                        value={formData.jd_education}
                        onChange={(e) => handleInputChange('jd_education', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., Bachelor's in Computer Science or equivalent"
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>

                {/* Responsibilities */}
                <div className={manualSection === 'complementaire' ? 'space-y-4' : 'hidden'}>
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <Users className="mr-2" size={20} />
                    Responsabilités
                  </h3>
                  
                  <div>
                    <label htmlFor="responsibility-input" className="block text-sm font-medium text-gray-700 mb-2">
                      Ajouter une responsabilité
                    </label>
                    <div className="flex space-x-2">
                      <input
                        id="responsibility-input"
                        type="text"
                        value={responsibilityInput}
                        onChange={(e) => setResponsibilityInput(e.target.value)}
                        onKeyPress={(e) => handleKeyPress(e, 'jd_responsibilities', responsibilityInput, setResponsibilityInput)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., Develop and maintain web applications"
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={() => addArrayItem('jd_responsibilities', responsibilityInput, setResponsibilityInput)}
                        disabled={!responsibilityInput.trim() || loading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Add
                      </button>
                    </div>
                    
                    {formData.jd_responsibilities.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {formData.jd_responsibilities.map((responsibility, index) => (
                          <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md">
                            <span className="text-sm text-gray-700">• {responsibility}</span>
                            <button
                              type="button"
                              onClick={() => removeArrayItem('jd_responsibilities', responsibility)}
                              className="text-red-600 hover:text-red-800"
                              disabled={loading}
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Skills */}
                <div className={manualSection === 'complementaire' ? 'space-y-6' : 'hidden'}>
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <Award className="mr-2" size={20} />
                    Compétences attendues
                  </h3>
                  
                  {/* Required Skills */}
                  <div>
                    <span className="block text-sm font-medium text-gray-700 mb-2">
                      Compétences requises
                    </span>
                    <TagInput
                      items={formData.jd_required_skills}
                      onAdd={(value) => setFormData(prev => ({ ...prev, jd_required_skills: [...prev.jd_required_skills, value] }))}
                      onRemove={(value) => removeArrayItem('jd_required_skills', value)}
                      placeholder="e.g., React, JavaScript, Node.js"
                      color="red"
                      disabled={loading}
                    />
                  </div>

                  {/* Desired Skills */}
                  <div>
                    <span className="block text-sm font-medium text-gray-700 mb-2">
                      Compétences souhaitées
                    </span>
                    <TagInput
                      items={formData.jd_desired_skills}
                      onAdd={(value) => setFormData(prev => ({ ...prev, jd_desired_skills: [...prev.jd_desired_skills, value] }))}
                      onRemove={(value) => removeArrayItem('jd_desired_skills', value)}
                      placeholder="e.g., TypeScript, GraphQL, Docker"
                      color="green"
                      disabled={loading}
                    />
                  </div>

                  {/* Soft Skills */}
                  <div>
                    <span className="block text-sm font-medium text-gray-700 mb-2">
                      Compétences comportementales
                    </span>
                    <TagInput
                      items={formData.jd_soft_skills}
                      onAdd={(value) => setFormData(prev => ({ ...prev, jd_soft_skills: [...prev.jd_soft_skills, value] }))}
                      onRemove={(value) => removeArrayItem('jd_soft_skills', value)}
                      placeholder="e.g., Communication, Leadership, Problem Solving"
                      color="purple"
                      disabled={loading}
                    />
                </div>
              </div>

            </form>
            )}

            {/* Duplicate Need Tab */}
            {activeTab === 'duplicate' && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Copier un besoin existant
                  </h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Recherchez un besoin existant pour créer une copie indépendante.
                  </p>
                </div>

                {/* Search Section */}
                <div className="space-y-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                       aria-label="Rechercher un besoin"
                       placeholder="Rechercher par intitulé, client ou description…"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={searchingNeeds}
                    />
                  </div>

                  {/* Needs List */}
                  <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                    {searchingNeeds ? (
                      <div className="p-4 text-center text-gray-500">
                        <Loader2 className="animate-spin mx-auto mb-2" size={20} />
                         Chargement des besoins…
                      </div>
                    ) : filteredNeeds.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                         {searchTerm ? 'Aucun besoin ne correspond à votre recherche.' : 'Aucun besoin disponible.'}
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {filteredNeeds.map((need) => (
                          <button
                            type="button"
                            key={need.id}
                            onClick={() => handleNeedSelect(need)}
                            className={`w-full p-4 text-left cursor-pointer hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-600 ${
                              selectedNeed?.id === need.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-medium text-gray-900">{need.title}</h4>
                                <p className="text-sm text-gray-600">{need.client}</p>
                                {need.description && (
                                  <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                                    {need.description}
                                  </p>
                                )}
                                <div className="flex items-center gap-2 mt-2">
                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                    need.priority === 'P1' ? 'bg-red-100 text-red-800' :
                                    need.priority === 'P2' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-green-100 text-green-800'
                                  }`}>
                                    {need.priority}
                                  </span>
                                  {need.tjm && (
                                    <span className="text-xs text-gray-500">
                                       {need.tjm} € / jour
                                    </span>
                                  )}
                                </div>
                              </div>
                              {selectedNeed?.id === need.id && (
                                <CheckCircle className="text-blue-600" size={20} />
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Selected Need Form */}
                {selectedNeed && (
                  <form id="need-duplicate-form" onSubmit={handleDuplicateSubmit} className="space-y-8">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-medium text-blue-900 mb-2">Besoin sélectionné</h4>
                      <p className="text-sm text-blue-700">
                        Copie de <strong>{selectedNeed.title}</strong> pour {selectedNeed.client}
                      </p>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-700">
                      <strong className="block text-gray-900">Ce qui sera copié</strong>
                      <p className="mt-1">Titre, description, client, classification, conditions, contacts, Salesforce et analyse de la fiche de poste.</p>
                      <strong className="mt-3 block text-gray-900">Ce qui repart à zéro</strong>
                      <p className="mt-1">Candidats associés, colonne du pipeline et progression.</p>
                    </div>

                    {/* Basic Information */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-gray-900 flex items-center">
                        <FileText className="mr-2" size={20} />
                         Informations essentielles
                      </h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Title Field */}
                        <div>
                          <label htmlFor="duplicate-title" className="block text-sm font-medium text-gray-700 mb-2">
                             Titre du besoin <span className="text-red-500">*</span>
                          </label>
                          <input
                            id="duplicate-title"
                            type="text"
                            value={duplicateFormData.title}
                            onChange={(e) => setDuplicateFormData(prev => ({ ...prev, title: e.target.value }))}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                              errors.title ? 'border-red-300 bg-red-50' : 'border-gray-300'
                            }`}
                            placeholder="e.g., Senior React Developer"
                            disabled={loading}
                            required
                          />
                          {errors.title && (
                            <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                          )}
                        </div>

                        {/* Client Field */}
                        <div>
                          <label htmlFor="duplicate-client" className="block text-sm font-medium text-gray-700 mb-2">
                            Client <span className="text-red-500">*</span>
                          </label>
                          <input
                            id="duplicate-client"
                            type="text"
                            value={duplicateFormData.client}
                            onChange={(e) => setDuplicateFormData(prev => ({ ...prev, client: e.target.value }))}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                              errors.client ? 'border-red-300 bg-red-50' : 'border-gray-300'
                            }`}
                            placeholder="e.g., Acme Corporation"
                            disabled={loading}
                            required
                          />
                          {errors.client && (
                            <p className="mt-1 text-sm text-red-600">{errors.client}</p>
                          )}
                        </div>

                        {/* Priority Field */}
                        <div>
                          <label htmlFor="duplicate-priority" className="block text-sm font-medium text-gray-700 mb-2">
                             Priorité
                          </label>
                          <select
                            id="duplicate-priority"
                            value={duplicateFormData.priority}
                            onChange={(e) => setDuplicateFormData(prev => ({ ...prev, priority: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            disabled={loading}
                          >
                             <option value="P1">P1 - Priorité haute</option>
                             <option value="P2">P2 - Priorité moyenne</option>
                             <option value="P3">P3 - Priorité basse</option>
                          </select>
                        </div>

                        {/* TJM Field */}
                        <div>
                          <label htmlFor="duplicate-tjm" className="block text-sm font-medium text-gray-700 mb-2">
                            TJM € <span className="text-red-500">*</span>
                          </label>
                          <input
                            id="duplicate-tjm"
                            type="number"
                            step="0.01"
                            min="0.01"
                            value={duplicateFormData.tjm}
                            onChange={(e) => setDuplicateFormData(prev => ({ ...prev, tjm: e.target.value }))}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                              errors.tjm ? 'border-red-300 bg-red-50' : 'border-gray-300'
                            }`}
                            placeholder="e.g., 650"
                            disabled={loading}
                            required
                          />
                          {errors.tjm && (
                            <p className="mt-1 text-sm text-red-600">{errors.tjm}</p>
                          )}
                        </div>
                      </div>

                      {/* Description Field */}
                      <div>
                        <label htmlFor="duplicate-description" className="block text-sm font-medium text-gray-700 mb-2">
                           Description générale
                        </label>
                        <textarea
                          id="duplicate-description"
                          value={duplicateFormData.description}
                          onChange={(e) => setDuplicateFormData(prev => ({ ...prev, description: e.target.value }))}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="General job description..."
                          disabled={loading}
                        />
                      </div>

                      {/* Category and Quantity Fields */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="duplicate-category" className="block text-sm font-medium text-gray-700 mb-2">
                            Category
                          </label>
                          <select
                            id="duplicate-category"
                            value={duplicateFormData.category || ''}
                            onChange={(e) => setDuplicateFormData(prev => ({ ...prev, category: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            disabled={loading}
                          >
                            <option value="">Select category...</option>
                            <option value="developer">Developer</option>
                            <option value="test">Test</option>
                            <option value="functionnal">Functional</option>
                            <option value="IT">IT</option>
                            <option value="devops">DevOps</option>
                            <option value="others">Others</option>
                          </select>
                        </div>
                        <div>
                          <label htmlFor="duplicate-quantity" className="block text-sm font-medium text-gray-700 mb-2">
                            Quantity
                          </label>
                          <input
                            type="number"
                            id="duplicate-quantity"
                            min="1"
                            value={duplicateFormData.quantity}
                            onChange={(e) => setDuplicateFormData(prev => ({ ...prev, quantity: Number.parseInt(e.target.value) || 1 }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="1"
                            disabled={loading}
                          />
                        </div>
                      </div>
                    </div>

                  </form>
                )}

                {/* Error for duplicate tab */}
                {errors.duplicate && (
                  <Alert variant="error">{errors.duplicate}</Alert>
                )}
              </div>
            )}
    </RecruitingCreateModalShell>
  );
};

export default NeedCreateModal;
